/**
 * Общий словарь событий матча.
 *
 * Лежит в `shared/types`, а не в модуле, потому что им пользуются сразу пятеро:
 * `overlay-v2` (рисует события), `template-editor` (выбор событий для табло),
 * `core` (строки Supabase), `client/tournaments` (публичный протокол) и `players`.
 * Пока он жил в `overlay-v2`, редактор шаблонов и оверлей импортировали друг друга
 * (IMGM-1401).
 */
export const MATCH_EVENT_TYPES = [
  'GOAL',
  'PASS_AND_GOAL',
  'PENALTY_GOAL',
  'PENALTY_MISSED',
  'AFTER_MATCH_PENALTY_GOAL',
  'AFTER_MATCH_PENALTY_MISSED',
  'OWN_GOAL',
  'FOUL',
  'REPLACEMENT',
  'TIME_OUT',
  'CORNER',
  'SAVE',
  'YELLOW_CARD',
  'RED_CARD',
] as const;

export type MatchEventType = (typeof MATCH_EVENT_TYPES)[number];

const MATCH_EVENT_TYPE_SET: ReadonlySet<string> = new Set(MATCH_EVENT_TYPES);

/** Строка из чужих данных (файл, бэкенд) — известный тип события журнала. */
export function isMatchEventType(value: unknown): value is MatchEventType {
  return typeof value === 'string' && MATCH_EVENT_TYPE_SET.has(value);
}

export type MatchEventTypeResponseDto =
  | 'goal'
  | 'penalty'
  | 'corner'
  | 'timeout'
  | 'foul'
  | 'owngoal'
  | 'replacement'
  | 'yellowcard'
  | 'redcard';

/**
 * Событие матча.
 *
 * Порядок `players` — часть контракта: у «паса и гола» это `[пасующий, забивающий]`,
 * у замены — `[уходит, заходит]` (так шлёт «Джойстик» и подписаны поля протокола, IMGM-1615),
 * у остальных событий игрок один и он же автор. Индекс автора гола считает
 * `getScorerIndex` (`shared/utils/match-event-players.ts`) — руками по нулевому индексу
 * гол не начисляют.
 */
export type MatchEvent = {
  // updateAt: number
  extraMinutes: number | null;
  id: number;
  minutes: number;
  period: number;
  seconds: number;
  teamId: number;
  type: MatchEventType;
  players: {
    id: number;
    number: number;
    teamId: number;
    fullName: string;
  }[];
};
